import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Tab, TabGroup, TabList } from '@headlessui/react';

export interface TabItem {
  id: string;
  label: string;
  count?: number;
  /** Class for the count, e.g. a status color; defaults to brand when the tab is active. */
  countClassName?: string;
  icon?: ReactNode;
  /** Trailing node, e.g. a pending-sync dot. */
  badge?: ReactNode;
  testId?: string;
}

interface TabsProps {
  tabs: readonly TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  /** underline = page tabs; pill = in-card filter with counts (DESIGN.md §1.4). */
  variant?: 'underline' | 'pill';
  ariaLabel?: string;
  /**
   * Gives tab and panel stable ids: tab `<prefix>-tab-<id>` (also its test id), panel
   * `<prefix>-panel-<id>`. The caller renders the `role="tabpanel"` with the panel id.
   */
  idPrefix?: string;
  className?: string;
}

const UNDERLINE = {
  list: 'flex space-x-8 -mb-px',
  base: 'focus-ring py-4 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center gap-1.5',
  active: 'border-brand-500 text-brand-600 dark:text-brand-400',
  inactive:
    'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600',
};

const PILL = {
  list: 'inline-flex bg-gray-100 dark:bg-gray-800/50 rounded-lg p-1',
  base: 'focus-ring px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap inline-flex items-center gap-1.5',
  active: 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white/90',
  inactive: 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
};

/** Roles, roving tabindex and arrow keys come from Headless UI; selection is controlled by the caller. */
export function Tabs({
  tabs,
  activeId,
  onChange,
  variant = 'underline',
  ariaLabel,
  idPrefix,
  className,
}: TabsProps) {
  const fallbackPrefix = useId();
  const prefix = idPrefix ?? fallbackPrefix;
  const listRef = useRef<HTMLDivElement>(null);
  const styles = variant === 'underline' ? UNDERLINE : PILL;
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === activeId),
  );

  // Headless UI only derives aria-controls from its own TabPanel; callers render the panel, so wire it here.
  useEffect(() => {
    listRef.current?.querySelectorAll('[role="tab"]').forEach((el, i) => {
      if (tabs[i]) el.setAttribute('aria-controls', `${prefix}-panel-${tabs[i].id}`);
    });
  });

  return (
    <TabGroup selectedIndex={selectedIndex} onChange={(index) => onChange(tabs[index].id)}>
      <TabList
        ref={listRef}
        aria-label={ariaLabel}
        className={className ? `${styles.list} ${className}` : styles.list}
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          const tabId = `${prefix}-tab-${tab.id}`;
          return (
            <Tab
              key={tab.id}
              id={tabId}
              data-testid={tab.testId ?? (idPrefix ? tabId : undefined)}
              className={`${styles.base} ${active ? styles.active : styles.inactive}`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={
                    tab.countClassName ?? (variant === 'pill' && active ? 'text-brand-500' : '')
                  }
                >
                  {tab.count}
                </span>
              )}
              {tab.badge}
            </Tab>
          );
        })}
      </TabList>
    </TabGroup>
  );
}
